import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFormState } from 'react-final-form';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { PRIMARY_COLOR } from '../../../../theme/colorConstants';
import { epochToDateInReadableFormat } from '../../../../utils';
import { MAX_JOBS_TOTAL } from '../../constDicts';
import FormSectionDescription from '../CustomComponents/FormSectionDescription';
import FormSectionHeader from '../CustomComponents/FormSectionHeader';

const DatePickerOpenings = ({ wrapperSx }) => {
  const form = useForm();
  const formState = useFormState();
  const theme = useTheme();

  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get the form values for dates and openings
  const formValues = formState.values;
  const datesOpenings = useMemo(() => formValues.datesOpenings || [], [formValues.datesOpenings]);

  const handleDateClick = useCallback(
    (date) => {
      // Store as epoch milliseconds for form submission
      const epochTime = date.getTime();

      const existingIndex = datesOpenings.findIndex((item) => item.date === epochTime);

      if (existingIndex >= 0) {
        // Remove date if already selected
        const newDatesOpenings = datesOpenings.filter((_, index) => index !== existingIndex);
        form.change('datesOpenings', newDatesOpenings);
      } else {
        // Check if adding this date would exceed the max jobs per month limit
        if (datesOpenings.length >= MAX_JOBS_TOTAL) {
          // Don't add the date if we're already at the limit
          return;
        }
        // Add new date with default opening of 1 and sort by date
        const newDatesOpenings = [...datesOpenings, { date: epochTime, openings: 1 }].sort(
          (a, b) => a.date - b.date
        );
        form.change('datesOpenings', newDatesOpenings);
      }
    },
    [datesOpenings, form]
  );

  const handleOpeningsChange = useCallback(
    (epochTime, newValue) => {
      const numValue = parseInt(newValue, 10);
      if (Number.isNaN(numValue) || numValue < 0 || numValue > 10000) return;

      const newDatesOpenings = datesOpenings.map((item) =>
        item.date === epochTime ? { ...item, openings: numValue } : item
      );
      form.change('datesOpenings', newDatesOpenings);
    },
    [datesOpenings, form]
  );

  const renderDay = (date) => {
    const epochTime = date.getTime();
    const isSelected = datesOpenings.some((item) => item.date === epochTime);

    return (
      <Box
        key={epochTime}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isSelected ? 'white' : 'text.primary',
            cursor: 'pointer',
            borderRadius: '50%',
            backgroundColor: isSelected ? PRIMARY_COLOR[70] : 'transparent',
            width: 39,
            height: 39,
            minWidth: 36,
            '&:hover': {
              backgroundColor: isSelected ? PRIMARY_COLOR[80] : 'rgba(0, 0, 0, 0.04)',
            },
          }}
          onClick={() => handleDateClick(date)}
        >
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            {date.getDate()}
          </Typography>
          {isSelected && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'white',
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', ...wrapperSx }}>
      <FormSectionHeader messageKey="job.create.datePickerOpenings.header" />
      <FormSectionDescription messageKey="job.create.datePickerOpenings.desc" />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 2,
              }}
            >
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={currentMonth}
                renderDay={renderDay}
                onChange={(newValue) => {
                  if (newValue) {
                    setCurrentMonth(newValue);
                  }
                }}
                renderInput={() => null}
                showToolbar={false}
                toolbarFormat=""
              />
            </Box>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 2,
              backgroundColor: 'background.paper',
              maxHeight: '368px',
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {LanguageConverterV2('job.create.datePickerOpenings.dateAndOpenings')}
            </Typography>

            {(() => {
              if (datesOpenings.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    {LanguageConverterV2('job.create.datePickerOpenings.selectDatesMessage')}
                  </Typography>
                );
              }

              const renderDateList = () => (
                <Box>
                  <Grid container sx={{ mb: 1, fontWeight: 600 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {LanguageConverterV2('job.create.datePickerOpenings.date')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {LanguageConverterV2('job.create.datePickerOpenings.openings')}
                      </Typography>
                    </Grid>
                  </Grid>
                  {datesOpenings.map((item) => (
                    <Grid container key={item.date} sx={{ mb: 1, alignItems: 'center' }}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          {epochToDateInReadableFormat(item.date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          type="number"
                          size="small"
                          value={item.openings}
                          onChange={(e) => handleOpeningsChange(item.date, e.target.value)}
                          inputProps={{
                            min: 0,
                            max: 10000,
                            step: 1,
                          }}
                          sx={{
                            width: '80px',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '5px',
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              );

              if (datesOpenings.length >= MAX_JOBS_TOTAL) {
                return (
                  <Box>
                    <Typography variant="body2" color="error" sx={{ mb: 2, fontWeight: 600 }}>
                      {LanguageConverterV2('job.create.validation.maxJobsPerMonth')}
                    </Typography>
                    {renderDateList()}
                  </Box>
                );
              }

              return renderDateList();
            })()}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

DatePickerOpenings.propTypes = {
  wrapperSx: PropTypes.shape({}),
};

export default DatePickerOpenings;
